import React from 'react'
import { Accordion, Button, Dropdown, Grid, Icon, Modal, Segment } from 'semantic-ui-react'
import { useTranslation } from 'react-i18next'

import { IStructure } from '@cbgms/api/response'
import { findKeyInObject } from '@cbgms/base/utils/helpers'
import { LocaleContext } from '@cbgms/base/locale/LocaleContext'
import { Locales } from '@carsys/enums/locales'
import { KnockTypes } from '@cbgms/components/grid/Knock/KnockTypes'

export interface IColumnSettingsDialogProps {
  structure: IStructure
  activeColumns: string[]
  open: boolean
  hideUUIDColumns: boolean
  onSettingAdd: (column: string, type: KnockTypes) => Promise<void>
  onSettingRemove: (columnName: string) => Promise<void>
  onSettingsReset: () => Promise<void>
  onSettingsDisplay: (key: string) => string
  onClose: () => void
}

type ColumnSettingsDialogProps = IColumnSettingsDialogProps

/** getListColumnKeys
 *  Will flatten a nested objects to a single key string
 *  IE: { key1: { key2: { key3:"val1" } } } => "key1.key2.key3"
 */
function getListColumnKeys(obj: any, parent?: string) {
  // flatten the list keys
  return Object.keys(obj).reduce<string[]>((accum: string[], key: string) => {
    const propName = parent ? `${parent}.${key}` : key
    if (typeof obj[key] === 'object') {
      accum = [...accum, ...getListColumnKeys(obj[key], propName)]
    } else {
      accum = [...accum, propName]
    }
    return accum
  }, [] as string[])
}

export const ColumnSettingsDialog: React.FC<ColumnSettingsDialogProps> = props => {
  const { t } = useTranslation()
  const [activeIndex, setActiveIndex] = React.useState(0)
  const { locale } = React.useContext(LocaleContext)
  const localeForComparison = !locale || locale === Locales.development ? Locales.nl_NL : locale

  return (
    <Modal open={props.open} onClose={props.onClose} closeIcon>
      <Modal.Header>{t('ColumnSettingsDialog.Title')}</Modal.Header>
      <Modal.Content>
        <Accordion styled fluid>
          {Object.keys(props.structure).map((entityName, i) => (
            <React.Fragment key={entityName}>
              <Accordion.Title active index={i} onClick={() => setActiveIndex(i)}>
                {t(`ColumnSettingsDialog.Entities.${entityName}`)}
              </Accordion.Title>
              <Accordion.Content style={{ minHeight: '300px', maxHeight: '500px', overflowY: 'scroll' }} active={activeIndex === i}>
                <Grid>
                  <Grid.Row>
                    <Grid.Column width={8}>
                      <Dropdown
                        className='selection'
                        fluid
                        search
                        selection
                        text=''
                        value=''
                        scrolling
                        selectOnBlur={false}
                        onChange={(_, { value }) => {
                          const columnName = value as string
                          const type = findKeyInObject(props.structure, columnName) as KnockTypes
                          props.onSettingAdd(columnName, type)
                        }}
                        options={getListColumnKeys(props.structure[entityName], entityName)
                          .filter(
                            column =>
                              !props.activeColumns.includes(column) &&
                              (!column.includes('UUID') || !props.hideUUIDColumns) &&
                              // enforce some columns to always be hidden.
                              !column.includes('Credentials') && // order accounts
                              !column.includes('Password') // rdw providers, ...
                          )
                          .map(column => ({
                            value: column,
                            text: props.onSettingsDisplay ? props.onSettingsDisplay(column) : column
                          }))
                          .sort((a, b) => a.text.localeCompare(b.text, localeForComparison, { sensitivity: 'base' }))}
                      />
                    </Grid.Column>
                    <Grid.Column width={8}>
                      {props.activeColumns
                        .filter(col => col && col.split('.')[0] === entityName)
                        .map(column => (
                          <Segment key={column}>
                            {props.onSettingsDisplay ? props.onSettingsDisplay(column) : column}

                            <Icon
                              name='close'
                              style={{ float: 'right' }}
                              onClick={() => props.onSettingRemove && props.onSettingRemove(column)}
                            />
                          </Segment>
                        ))}
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </Accordion.Content>
            </React.Fragment>
          ))}
        </Accordion>
      </Modal.Content>
      <Modal.Actions className='flex-box'>
        <Button content={t('ColumnSettingsDialog.Reset')} onClick={props.onSettingsReset} negative className='flex-pull-left' />
        {props.activeColumns.length === 0 && (
          <span className='flex-inline align-items-center error'>{t('ColumnSettingsDialog.ErrorNoColumns')}</span>
        )}
        <Button content={t('ColumnSettingsDialog.Done')} disabled={props.activeColumns.length === 0} onClick={props.onClose} positive />
      </Modal.Actions>
    </Modal>
  )
}

export default React.memo(ColumnSettingsDialog)
